import { apiGet, apiPost } from './client';

/**
 * Phase 115 (Plan 115-06 / PCOPS-02) — the `/admin/robot-tasks` API client.
 * Types mirror `backend/src/api/schemas/botRobotTasks.ts`'s `robotTaskRowSchema`
 * / `robotTaskActionResponse` EXACTLY — every field the backend always emits
 * (even as `null`) is listed here too.
 */

export type RoutingExecutor = 'monka' | 'paperclip';
export type MonkaExecutionState = 'claimed' | 'in_flight' | 'released';

/** Phase 119 (Plan 119-03) — mirrors `pendingQuestion` in `botRobotTasks.ts`. */
export interface PendingQuestionSummary {
  requestId: number;
  title: string | null;
  recipientName: string | null;
  escalationName: string | null;
  askedAt: string;
  remindedAt: string | null;
  escalatedAt: string | null;
  dmStatus: string;
}

export interface RobotTaskRow {
  taskId: number;
  taskCode: string | null;
  taskName: string;
  projectId: number;
  projectName: string | null;
  executor: RoutingExecutor | null;
  routingSource: string | null;
  executorState: string | null;
  executorStateAt: string | null;
  monkaExecutionState: MonkaExecutionState | null;
  paperclipDispatchStatus: string | null;
  paperclipRunStatus: string | null;
  paperclipRunStatusAt: string | null;
  paperclipIssueId: string | null;
  paperclipDispatchAttempt: number;
  botClaimedAt: string | null;
  assigneeId: number | null;
  assigneeName: string | null;
  stale: boolean;
  /** The newest pending Factory™ question on the task, or null. */
  pendingQuestion: PendingQuestionSummary | null;
}

export interface RobotTaskFilters {
  executor?: RoutingExecutor;
  state?: string;
  projectId?: number;
  limit?: number;
}

export interface RobotTaskActionResult {
  taskId: number;
  allowed: boolean;
  reason: string | null;
  attempt: number | null;
}

function buildQuery(filters: RobotTaskFilters): string {
  const params = new URLSearchParams();
  if (filters.executor) params.set('executor', filters.executor);
  if (filters.state) params.set('state', filters.state);
  if (filters.projectId !== undefined) params.set('projectId', String(filters.projectId));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getRobotTasks(filters: RobotTaskFilters = {}): Promise<RobotTaskRow[]> {
  const res = await apiGet<{ data: RobotTaskRow[] }>(`/admin/robot-tasks${buildQuery(filters)}`);
  return res.data;
}

export async function cancelRobotTask(taskId: number): Promise<RobotTaskActionResult> {
  const res = await apiPost<{ data: RobotTaskActionResult }>(
    `/admin/robot-tasks/${taskId}/cancel`,
  );
  return res.data;
}

export async function retryRobotTask(taskId: number): Promise<RobotTaskActionResult> {
  const res = await apiPost<{ data: RobotTaskActionResult }>(
    `/admin/robot-tasks/${taskId}/retry`,
  );
  return res.data;
}
